/**
 * Centralized Events Configuration
 * 
 * This file defines all wedding events with their properties and enabled state.
 * To show/hide events, simply toggle the `enabled` property.
 * 
 * Usage:
 * - Schedule section will only render enabled events
 * - RSVP form will only show enabled events as options
 * - Admin dashboard will respect enabled events
 */

export interface EventConfig {
  key: 'engagement' | 'sangeet' | 'wedding' | 'finalSoiree';
  enabled: boolean;
  // Schedule section properties
  title: string;
  date: string;
  city: string;
  description: string;
  venue: string;
  schedule: readonly string[];
  includes: string;
  directionsUrl: string;
  // RSVP section properties
  rsvpLabel: string;
}

/**
 * Master Events Configuration
 * Toggle the `enabled` property to show/hide events throughout the app
 */
export const EVENTS_CONFIG: Record<string, EventConfig> = {
  engagement: {
    key: 'engagement',
    enabled: true,
    // Schedule section data
    title: 'Shubh Aarambh - Engagement',
    date: '22nd December, 2025',
    city: 'Mumbai',
    description: 'Let\'s make it official with some pre-wedding rituals, and awkward introductions 😀',
    venue: 'At Zaika Banquets, Borivali West, Mumbai',
    schedule: ['8:30 AM Onwards'],
    includes: 'Come hungry! Breakfast and lunch will be served with love',
    directionsUrl: 'https://maps.app.goo.gl/zjCfWdddYYyDfajF7',
    // RSVP section data
    rsvpLabel: 'Engagement (Mumbai, 22nd Dec)',
  },

  sangeet: {
    key: 'sangeet',
    enabled: true,
    // Schedule section data
    title: 'Rang aur Taal - Haldi & Sangeet',
    date: '27th December, 2025',
    city: 'Pune',
    description: 'Two cities, one shade of Yellow 💛',
    venue: 'At Marigold Banquets (Magnolia), Bavdhan, Pune',
    schedule: ['5:30 PM Onwards'],
    includes: 'We\'ll end the night with dinner under the stars - good food, laughter, and love all around.',
    directionsUrl: 'https://maps.app.goo.gl/scciF89ZpxZN4cXMA',
    // RSVP section data
    rsvpLabel: 'Haldi & Sangeet (Pune, 27th Dec)',
  },

  wedding: {
    key: 'wedding',
    enabled: true,
    // Schedule section data
    title: 'Bandhan Forever Wala - Wedding',
    date: '28th December, 2025',
    city: 'Pune',
    description: 'From Bombay to Pune, we\'re closing the distance with vows and vada pavs!',
    venue: 'At Marigold Banquets (Mimosa), Bavdhan, Pune',
    schedule: ['9:30 AM Onwards'],
    includes: 'Come hungry - breakfast and lunch are on us!',
    directionsUrl: 'https://maps.app.goo.gl/scciF89ZpxZN4cXMA',
    // RSVP section data
    rsvpLabel: 'Wedding (Pune, 28th Dec)',
  },

  finalSoiree: {
    key: 'finalSoiree',
    enabled: true,
    // Schedule section data
    title: 'The Happily Ever After Party - Reception',
    date: '2nd January, 2026',
    city: 'Mumbai',
    description: 'A final soirée filled with music, laughter, and celebration as we come together to dance, dine, and toast to forever.',
    venue: 'At Arcadia Banquets, Borivali West, Mumbai',
    schedule: ['7:00 PM Onwards'],
    includes: 'No need to worry about dinner plans. We’ve got a seat (and a plate) waiting for you.',
    directionsUrl: 'https://maps.app.goo.gl/YNHSLZMrBKtpsXoL8',
    // RSVP section data
    rsvpLabel: 'Reception (Mumbai, 2nd Jan)',
  },
} as const;

/**
 * Helper functions to work with events configuration
 */
export const getEnabledEvents = () => {
  return Object.values(EVENTS_CONFIG).filter(event => event.enabled);
};

export const getEventByKey = (key: string) => {
  return EVENTS_CONFIG[key];
};

export const getEnabledEventKeys = () => {
  return Object.keys(EVENTS_CONFIG).filter(key => EVENTS_CONFIG[key].enabled);
};

export const getEnabledEventsForRSVP = () => {
  return getEnabledEvents().map(event => ({
    key: event.key,
    label: event.rsvpLabel,
  }));
};

export const getEnabledEventsForSchedule = () => {
  return getEnabledEvents().map(event => ({
    key: event.key,
    title: event.title,
    date: event.date,
    city: event.city,
    description: event.description,
    venue: event.venue,
    schedule: event.schedule,
    includes: event.includes,
    directionsUrl: event.directionsUrl,
  }));
};

/**
 * Get accommodation options based on enabled events
 * - Pune option shows if both sangeet and wedding are enabled
 * - Mumbai option shows if finalSoiree is enabled
 */
export const getEnabledAccommodationOptions = () => {
  const options: Array<{value: string, label: string}> = [];
  
  // Show Pune accommodation if both Pune events (sangeet and wedding) are enabled
  const sangeetEnabled = EVENTS_CONFIG.sangeet.enabled;
  const weddingEnabled = EVENTS_CONFIG.wedding.enabled;
  
  if (sangeetEnabled && weddingEnabled) {
    options.push({
      value: 'pune',
      label: 'Pune - Haldi, Sangeet & Wedding (27th Night)',
    });
  }
  
  // Show Mumbai accommodation if finalSoiree (reception) is enabled
  if (EVENTS_CONFIG.finalSoiree.enabled) {
    options.push({
      value: 'mumbai',
      label: 'Mumbai - Reception (2nd Night)',
    });
  }
  
  return options;
};

/**
 * Location-based filtering functions
 */
export type LocationFilter = 'all' | 'pune' | 'mumbai' | 'main' | 'reception';

export const getEventsByLocation = (location: LocationFilter) => {
  const enabledEvents = getEnabledEvents();
  
  if (location === 'all') {
    return enabledEvents;
  }
  
  if (location === 'main') {
    // Main events exclude engagement (skip the first event)
    return enabledEvents.filter(event => event.key !== 'engagement');
  }
  
  if (location === 'reception') {
    // Reception route only shows the finalSoiree event
    return enabledEvents.filter(event => event.key === 'finalSoiree');
  }
  
  return enabledEvents.filter(event => 
    event.city.toLowerCase().includes(location.toLowerCase())
  );
};

export const getEventsForRSVPByLocation = (location: LocationFilter) => {
  return getEventsByLocation(location).map(event => ({
    key: event.key,
    label: event.rsvpLabel,
  }));
};

export const getEventsForScheduleByLocation = (location: LocationFilter) => {
  return getEventsByLocation(location).map(event => ({
    key: event.key,
    title: event.title,
    date: event.date,
    city: event.city,
    description: event.description,
    venue: event.venue,
    schedule: event.schedule,
    includes: event.includes,
    directionsUrl: event.directionsUrl,
  }));
};

export const getAccommodationOptionsByLocation = (location: LocationFilter) => {
  const allOptions = getEnabledAccommodationOptions();
  
  if (location === 'all' || location === 'main') {
    // Both 'all' and 'main' show all accommodation options since main events span both cities
    return allOptions;
  }
  
  if (location === 'reception') {
    // Reception route only shows Mumbai accommodation
    return allOptions.filter(option => option.value === 'mumbai');
  }
  
  // Filter accommodation options by location
  return allOptions.filter(option => 
    option.value === location
  );
};